import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Plus, Pencil, Trash2, Download, Upload, Check, X, KeyRound, Search, Sun, Moon, BookOpen, BrainCircuit } from 'lucide-react';
import { ConfirmModal } from './ConfirmModal';
import { useT } from '../i18n';
import { useSettingsStore } from '../stores/settingsStore';
import { fuzzyFilter } from '../utils/fuzzy';

const HighlightedName = ({ name, indices }) => {
  if (!indices.length) return <span>{name}</span>;
  const indexSet = new Set(indices);
  return (
    <span>
      {[...name].map((char, i) =>
        indexSet.has(i)
          ? <span key={i} style={{ color: 'var(--accent)', fontWeight: 800 }}>{char}</span>
          : <span key={i}>{char}</span>
      )}
    </span>
  );
};

export const MapSwitcher = ({
  maps,
  currentMapId,
  currentMapName,
  onSwitchMap,
  onCreateMap,
  onDeleteMap,
  onRenameMap,
  onExportMap,
  onImportMap,
  onImportFromBook,
  onStartQuiz,
  onChangeProvider,
  providerName
}) => {
  const t = useT();
  const lang = useSettingsStore((s) => s.lang);
  const toggleTheme = useSettingsStore((s) => s.toggleTheme);
  const isLight = useSettingsStore((s) => s.isLight);
  const [isOpen, setIsOpen] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [confirmMap, setConfirmMap] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const buttonRef = useRef(null);
  const fileInputRef = useRef(null);
  const searchRef = useRef(null);
  const activeItemRef = useRef(null);

  const sortedMaps = useMemo(
    () => [...maps].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
    [maps]
  );

  const filteredResults = useMemo(
    () => fuzzyFilter(searchQuery, sortedMaps, m => m.name),
    [searchQuery, sortedMaps]
  );

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setActiveIndex(0); }, [searchQuery]);

  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    const handleGlobalKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (!isOpen && buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setDropdownPosition({ top: rect.bottom + 8, left: rect.left });
          setSearchQuery('');
          setActiveIndex(0);
        }
        setIsOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [isOpen]);

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleRenameStart = (e, map) => {
    e.stopPropagation();
    setRenamingId(map.id);
    setRenameValue(map.name);
  };

  const handleRenameConfirm = (e) => {
    e.stopPropagation();
    if (renameValue.trim()) onRenameMap(renameValue.trim());
    setRenamingId(null);
  };

  const handleRenameCancel = (e) => {
    e.stopPropagation();
    setRenamingId(null);
  };

  const handleDelete = (e, mapId) => {
    e.stopPropagation();
    setConfirmMap(mapId);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await onImportMap(file);
      setIsOpen(false);
    } catch (err) {
      window.alert(err.message);
    }
    fileInputRef.current.value = '';
  };

  const handleCreate = () => {
    onCreateMap();
    setIsOpen(false);
  };

  const handleSwitch = (mapId) => {
    onSwitchMap(mapId);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom + 8, left: rect.left });
      setSearchQuery('');
      setActiveIndex(0);
    }
    setIsOpen(!isOpen);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % Math.max(1, filteredResults.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => (i - 1 + filteredResults.length) % Math.max(1, filteredResults.length));
    } else if (e.key === 'Enter') {
      if (filteredResults[activeIndex]) handleSwitch(filteredResults[activeIndex].item.id);
    } else if (e.key === 'Escape') {
      if (searchQuery) setSearchQuery('');
      else setIsOpen(false);
    }
  };

  const mapToDelete = maps.find(m => m.id === confirmMap);
  const handleImportClick = () => fileInputRef.current?.click();
  const handleExportClick = () => { onExportMap(); setIsOpen(false); };

  return (
    <div className="relative w-full">
      <ConfirmModal
        isOpen={!!confirmMap}
        message={mapToDelete ? t('mapSwitcher.deleteConfirm', { name: mapToDelete.name }) : t('mapSwitcher.deleteGeneric')}
        danger
        onConfirm={() => { onDeleteMap(confirmMap); setConfirmMap(null); setIsOpen(false); }}
        onCancel={() => setConfirmMap(null)}
      />

      {/* Trigger button */}
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          padding: '8px 14px',
          background: 'var(--surface)',
          border: '2px solid var(--border-s)',
          borderRadius: '12px',
          color: 'var(--text-primary)',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'border-color 0.2s, background 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-s)'}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {currentMapName}
        </span>
        <ChevronDown
          size={15}
          style={{
            color: 'var(--text-muted)',
            flexShrink: 0,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.25s',
          }}
        />
      </button>

      {isOpen && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />

          <div
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: '320px',
              background: 'var(--bg-dim)',
              border: '1px solid var(--border)',
              borderRadius: '16px',
              boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
              zIndex: 9999,
              overflow: 'hidden',
            }}
          >
            {/* Search */}
            <div style={{ padding: '12px 12px 8px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: 'var(--surface)',
                  border: '1.5px solid var(--border-s)',
                  borderRadius: '10px',
                  transition: 'border-color 0.2s',
                }}
                onFocusCapture={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                onBlurCapture={e => e.currentTarget.style.borderColor = 'var(--border-s)'}
              >
                <Search size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <input
                  ref={searchRef}
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Cerca mappa…"
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    fontSize: '13px',
                    color: 'var(--text-primary)',
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 0 }}
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>

            {/* Map list */}
            <div style={{ maxHeight: '240px', overflowY: 'auto', paddingBottom: '4px' }}>
              {filteredResults.length === 0 ? (
                <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: '13px', color: 'var(--text-muted)' }}>
                  Nessuna mappa per <span style={{ fontWeight: 700, color: 'var(--accent)' }}>«{searchQuery}»</span>
                </div>
              ) : (
                filteredResults.map(({ item: map, indices }, index) => (
                  <div
                    key={map.id}
                    ref={index === activeIndex ? activeItemRef : null}
                    onClick={() => handleSwitch(map.id)}
                    onMouseEnter={() => setActiveIndex(index)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      margin: '1px 8px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      border: map.id === currentMapId
                        ? '1.5px solid var(--accent)'
                        : '1.5px solid transparent',
                      background: map.id === currentMapId
                        ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
                        : index === activeIndex
                          ? 'var(--surface)'
                          : 'transparent',
                      transition: 'background 0.12s, border-color 0.12s',
                    }}
                  >
                    {renamingId === map.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }} onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleRenameConfirm(e);
                            if (e.key === 'Escape') handleRenameCancel(e);
                          }}
                          style={{
                            flex: 1,
                            padding: '4px 10px',
                            fontSize: '13px',
                            background: 'var(--surface)',
                            border: '1.5px solid var(--accent)',
                            borderRadius: '8px',
                            outline: 'none',
                            color: 'var(--text-primary)',
                          }}
                          autoFocus
                        />
                        <button onClick={handleRenameConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--success)', display: 'flex', padding: '4px' }}>
                          <Check size={15} />
                        </button>
                        <button onClick={handleRenameCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: '4px' }}>
                          <X size={15} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <HighlightedName name={map.name} indices={indices} />
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                            {new Date(map.updatedAt).toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                        <div className="group-actions" style={{ display: 'flex', gap: '2px', opacity: index === activeIndex ? 1 : 0, transition: 'opacity 0.15s' }}>
                          {map.id === currentMapId && (
                            <button
                              onClick={e => handleRenameStart(e, map)}
                              title={t('mapSwitcher.rename')}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: '4px', borderRadius: '6px' }}
                              onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.background = 'var(--surface)'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'none'; }}
                            >
                              <Pencil size={14} />
                            </button>
                          )}
                          <button
                            onClick={e => handleDelete(e, map.id)}
                            title={t('mapSwitcher.delete')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: '4px', borderRadius: '6px' }}
                            onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.background = 'color-mix(in srgb, var(--danger) 12%, transparent)'; }}
                            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'none'; }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Actions */}
            <div style={{ borderTop: '1px solid var(--border-s)', paddingTop: '6px', paddingBottom: '6px' }}>
              <MenuAction icon={<Plus size={16} />} label={t('mapSwitcher.newMap')} onClick={handleCreate} iconColor="var(--success)" />
              <MenuAction icon={<BrainCircuit size={16} />} label="Quiz" onClick={() => { onStartQuiz?.(); setIsOpen(false); }} iconColor="var(--accent)" />
              <MenuAction icon={<BookOpen size={16} />} label={t('mapSwitcher.importBook')} onClick={() => { onImportFromBook?.(); setIsOpen(false); }} iconColor="#1e66f5" />
              <MenuAction icon={<Download size={16} />} label={t('mapSwitcher.exportJson')} onClick={handleExportClick} iconColor="var(--accent-2)" />
              <MenuAction icon={<Upload size={16} />} label={t('mapSwitcher.importJson')} onClick={handleImportClick} iconColor="var(--accent-2)" />
            </div>

            <div style={{ borderTop: '1px solid var(--border-s)', paddingTop: '6px', paddingBottom: '6px' }}>
              <MenuAction
                icon={<KeyRound size={16} />}
                label={t('mapSwitcher.changeProvider')}
                sublabel={providerName}
                onClick={() => { onChangeProvider(); setIsOpen(false); }}
                iconColor="var(--accent)"
              />
              <MenuAction
                icon={isLight ? <Moon size={16} /> : <Sun size={16} />}
                label={isLight ? 'Passa a Dark (Mocha)' : 'Passa a Light (Latte)'}
                onClick={() => { toggleTheme(); }}
                iconColor="var(--warning)"
              />
            </div>

            <input ref={fileInputRef} type="file" accept=".json" onChange={handleImport} className="hidden" />
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

const MenuAction = ({ icon, label, sublabel, onClick, iconColor }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '9px 14px',
        margin: '1px 8px',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: 600,
        color: 'var(--text-primary)',
        background: hover ? 'var(--surface)' : 'transparent',
        border: 'none',
        width: 'calc(100% - 16px)',
        cursor: 'pointer',
        transition: 'background 0.12s',
        textAlign: 'left',
      }}
    >
      <span style={{ color: iconColor, display: 'flex', flexShrink: 0 }}>{icon}</span>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span>{label}</span>
        {sublabel && <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 400, marginTop: '1px' }}>{sublabel}</span>}
      </div>
    </button>
  );
};
